import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import logo from '../../assets/logo.png';

const UpdatePassword = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handlePasswordRecovery = async () => {
            try {
                const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
                const queryParams = new URLSearchParams(location.search);

                const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
                const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
                const type = hashParams.get('type') || queryParams.get('type');

                if (!accessToken) {
                    const { data: { session } } = await supabase.auth.getSession();

                    if (!session) {
                        throw new Error('No recovery token found');
                    }
                }

                if (accessToken && refreshToken) {
                    const { data, error: sessionError } = await supabase.auth.setSession({
                        access_token: accessToken,
                        refresh_token: refreshToken
                    });

                    if (sessionError) throw sessionError;
                }

                setLoading(false);

            } catch (error) {
                console.error('Recovery error:', error);
                setError('Invalid or expired recovery link');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        };

        handlePasswordRecovery();
    }, [navigate, location]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (newPassword.length < 6) {
            return setError('Password must be at least 6 characters long');
        }

        try {
            setError('');
            setLoading(true);

            const { error } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (error) throw error;

            setSuccess(true);
            await supabase.auth.signOut();

            setTimeout(() => {
                navigate('/login', {
                    replace: true,
                    state: { message: 'Password updated successfully. Please log in with your new password.' }
                });
            }, 3000);

        } catch (error) {
            console.error('Update error:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <img
                        src={logo}
                        alt="Klasite Logo"
                        className="mx-auto h-12 w-auto mb-4"
                    />
                    <div className="text-lg">Setting up password reset...</div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
                    <div className="text-center">
                        <img
                            src={logo}
                            alt="Klasite Logo"
                            className="mx-auto h-12 w-auto mb-6"
                        />
                        <svg
                            className="mx-auto h-12 w-12 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                        <h2 className="mt-4 text-2xl font-bold text-gray-900">Password Updated Successfully!</h2>
                        <p className="mt-2 text-gray-600">
                            Your password has been updated. You will be redirected to the login page in a few seconds...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <img
                        src={logo}
                        alt="Klasite Logo"
                        className="mx-auto h-12 w-auto"
                    />
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Update your password
                    </h2>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                            {error}
                        </div>
                    )}

                    <div className="rounded-md shadow-sm space-y-4">
                        <div>
                            <label htmlFor="new-password" className="sr-only">
                                New Password
                            </label>
                            <input
                                id="new-password"
                                name="new-password"
                                type="password"
                                required
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder="New password"
                                minLength="6"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirm-password" className="sr-only">
                                Confirm New Password
                            </label>
                            <input
                                id="confirm-password"
                                name="confirm-password"
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder="Confirm new password"
                                minLength="6"
                            />
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {loading ? 'Updating...' : 'Update password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdatePassword;